import { StrictMode, startTransition } from 'react'
import { hydrateRoot } from 'react-dom/client'
import { StartClient } from '@tanstack/react-start/client'
import { isHydrationError } from './lib/hydration-mismatch'
import { markHydrationStarted } from './lib/hydration-rewrite-monitor'
import { logFrontendError } from './lib/log-error'

/**
 * Custom client entry, auto-detected as `src/client` and replacing the package default (see
 * `resolveEntry` in @tanstack/start-plugin-core — the same mechanism `src/server.ts` uses). It exists
 * for exactly one reason: to pass React's error hooks — `onRecoverableError` and `onUncaughtError` — to
 * `hydrateRoot`, so a hydration failure is reported with the `componentStack` that names our OWN
 * components instead of only React internals.
 *
 * Everything else is a verbatim copy of the default entry — `startTransition`, `StrictMode`, hydrating
 * `document`. Keep it that way, or hydration behaviour drifts from upstream for no reason.
 */

/**
 * Report the errors React *recovers* from, above all a hydration mismatch.
 *
 * Those are undiagnosable as they arrive today: in a production build the message is `Minified React
 * error #418` and the only stack is React's own internals inside `vendor-react-*.js`, so nothing says
 * WHICH text in WHICH component. `onRecoverableError` is the only place the missing detail exists —
 * React hands it an `errorInfo` carrying a `componentStack`, and every chunk ships a sourcemap, so
 * those frames resolve to real component names.
 *
 * Why this does not double-report: React's DEFAULT `onRecoverableError` calls `reportError(error)`,
 * which dispatches a window `error` event — what the global handler in `router.tsx` has been picking up
 * all along, without any of this context. Supplying our own replaces that call, so each recoverable
 * error is reported once, here, with the component stack. The `console.error` preserves the visibility
 * `reportError` used to give (and, since it happens before any filtering, a mismatch stays visible in
 * the browser even when the report is gated).
 *
 * `logFrontendError` owns the rest: the chunk-load and tRPC filters, and the single document-rewrite
 * gate that decides whether a hydration mismatch was a page translator's rather than ours.
 */
function reportWithComponentStack(
  resolvePrefix: (message: string) => string,
  error: unknown,
  errorInfo: { componentStack?: string | null },
): void {
  const message = error instanceof Error ? error.message : String(error)
  const namePrefix = resolvePrefix(message)
  console.error(namePrefix, error, errorInfo.componentStack)
  logFrontendError(error, 'recoverable', { namePrefix: `${namePrefix} `, componentStack: errorInfo.componentStack })
}

function reportRecoverableError(error: unknown, errorInfo: { componentStack?: string | null }): void {
  reportWithComponentStack((message) => (isHydrationError(message) ? '[HYDRATION]' : '[RECOVERABLE]'), error, errorInfo)
}

/**
 * Report the errors React does NOT recover from and no boundary caught.
 *
 * Without this they take React's default `onUncaughtError`, which calls `reportError(error)` and so
 * surfaces as a window `error` event — picked up by the global handler in `router.tsx`, which has no
 * access to React's `errorInfo` and therefore reports NO component stack. That is exactly what makes a
 * structural hydration mismatch (`#418 … args[]=HTML`) undiagnosable: React-internal frames in
 * `vendor-react-*.js` and nothing naming our own elements. Sourcemaps cannot help there, because the
 * frames genuinely belong to the reconciler.
 *
 * Supplying this handler replaces that `reportError` call, so the failure is reported ONCE — here, with
 * the component stack — rather than once from the window handler without it.
 *
 * Kept under its own `[UNCAUGHT]` prefix rather than reusing `[HYDRATION]`: the name feeds the server's
 * error fingerprint, and an error React could not recover from is a different severity from one it
 * patched over. Merging them would hide a broken page behind an issue for a cosmetic mismatch.
 */
function reportUncaughtError(error: unknown, errorInfo: { componentStack?: string | null }): void {
  reportWithComponentStack(() => '[UNCAUGHT]', error, errorInfo)
}

// Outside the transition and before it: every rewrite the monitor logs from here on could be React's
// own, and one logged earlier provably could not. See HYDRATION_STARTED_KEY.
markHydrationStarted()

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <StartClient />
    </StrictMode>,
    // `onCaughtError` is deliberately left at React's default: an error a boundary caught is already
    // reported by that boundary (see DefaultRouteError in route-error.tsx), so handling it here would
    // double-report.
    { onRecoverableError: reportRecoverableError, onUncaughtError: reportUncaughtError },
  )
})
